import React, { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Check, Loader2, Plus } from 'lucide-react';
import imageGenerationService, { GenerationSource, ImageGenerationJob, MagicResizeCategory, MagicResizePreset } from '../../services/api/imageGenerationService';
import { TaskySelect } from '../tasky/form/TaskySelect';

interface MagicResizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageId: number | null;
  imageUrl?: string | null;
  generationSource: Extract<GenerationSource, 'banner_ads' | 'image_generation'>;
  onSuccess?: (job: ImageGenerationJob) => void;
}

const MagicResizeModal: React.FC<MagicResizeModalProps> = ({
  isOpen,
  onClose,
  imageId,
  imageUrl,
  generationSource,
  onSuccess,
}) => {
  const [categories, setCategories] = useState<MagicResizeCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSizes, setSelectedSizes] = useState<Set<string>>(new Set());
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [guideInset, setGuideInset] = useState(10);

  // Fetch presets when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPresets();
    } else {
      // Reset state when modal closes
      setSelectedSizes(new Set());
      setCustomWidth('');
      setCustomHeight('');
      setError(null);
      setPreviewKey(null);
      setGuideInset(10);
    }
  }, [isOpen]);

  const fetchPresets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await imageGenerationService.getMagicResizePresets();
      if (response.success && response.data) {
        setCategories(response.data);
      } else {
        setError(response.message || 'プリセットの取得に失敗しました');
      }
    } catch (err: any) {
      console.error('Failed to fetch magic resize presets:', err);
      setError(err.response?.data?.message || 'プリセットの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getSizeKey = (width: number, height: number): string => {
    return `${width}x${height}`;
  };

  const handlePresetToggle = useCallback((preset: MagicResizePreset) => {
    const key = `preset:${preset.id}`;
    setSelectedSizes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const handleCategoryToggle = useCallback((category: MagicResizeCategory) => {
    const categoryKeys = category.presets.map((p) => `preset:${p.id}`);
    const allSelected = categoryKeys.every((key) => selectedSizes.has(key));

    setSelectedSizes((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all
        categoryKeys.forEach((key) => newSet.delete(key));
      } else {
        // Select all
        categoryKeys.forEach((key) => newSet.add(key));
      }
      return newSet;
    });
  }, [selectedSizes]);

  const handleAddCustomSize = useCallback(() => {
    const width = parseInt(customWidth);
    const height = parseInt(customHeight);

    if (isNaN(width) || isNaN(height)) {
      setError('幅と高さを正しく入力してください');
      return;
    }

    if (width < 1 || width > 10000 || height < 1 || height > 10000) {
      setError('サイズは1〜10000の範囲で指定してください');
      return;
    }

    const key = getSizeKey(width, height);
    setSelectedSizes((prev) => {
      const newSet = new Set(prev);
      newSet.add(key);
      return newSet;
    });

    setCustomWidth('');
    setCustomHeight('');
    setError(null);
  }, [customWidth, customHeight]);

  const handleExecute = async () => {
    if (!imageId || selectedSizes.size === 0) return;

    setExecuting(true);
    setError(null);

    try {
      const presetIds = Array.from(selectedSizes)
        .filter((key) => key.startsWith('preset:'))
        .map((key) => Number(key.slice(7)));
      const sizes = Array.from(selectedSizes).filter((key) => !key.startsWith('preset:')).map((key) => {
        const [width, height] = key.split('x').map(Number);
        return { width, height };
      });

      const response = await imageGenerationService.executeMagicResize(imageId, sizes, generationSource, presetIds);

      if (response.success && response.data?.job) {
        onSuccess?.(response.data.job);
        onClose();
      } else {
        setError(response.message || 'ジョブの登録に失敗しました');
      }
    } catch (err: any) {
      console.error('Failed to execute magic resize:', err);
      setError(err.response?.data?.message || 'マジックリサイズの実行に失敗しました');
    } finally {
      setExecuting(false);
    }
  };

  const isCategoryFullySelected = (category: MagicResizeCategory): boolean => {
    return category.presets.every((p) =>
      selectedSizes.has(`preset:${p.id}`)
    );
  };

  const isCategoryPartiallySelected = (category: MagicResizeCategory): boolean => {
    const selectedCount = category.presets.filter((p) =>
      selectedSizes.has(`preset:${p.id}`)
    ).length;
    return selectedCount > 0 && selectedCount < category.presets.length;
  };

  const previewOptions = [
    ...categories.flatMap((category) => category.presets
      .filter((preset) => selectedSizes.has(`preset:${preset.id}`))
      .map((preset) => ({
        key: `preset:${preset.id}`,
        label: `${category.name} / ${preset.name}`,
        width: preset.width,
        height: preset.height,
        name: preset.name,
      }))),
    ...Array.from(selectedSizes)
      .filter((key) => !key.startsWith('preset:'))
      .map((key) => {
        const [width, height] = key.split('x').map(Number);
        return { key, label: `カスタム / ${width}×${height}`, width, height, name: 'カスタム' };
      }),
  ];
  const preview = previewOptions.find((option) => option.key === previewKey) ?? previewOptions[0];
  const placementNote = preview?.name.includes('Instagram ストーリー')
    ? 'ストーリーは画面上の操作表示と重なる場合があります。上下の文字やロゴを特に確認してください。'
    : preview?.name.includes('LINE VOOM')
      ? 'LINE VOOM は掲載面によって表示比率が変わります。配信先の掲載仕様も確認してください。'
      : preview?.name.includes('Facebook')
        ? 'Facebook 広告は掲載面ごとに見え方が変わります。文字・ロゴが端に寄りすぎていないか確認してください。'
        : '配信先によって余白や表示範囲が変わります。文字・ロゴの位置を確認してください。';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              マジックリサイズ
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
              <span className="ml-3 text-gray-600">プリセットを読み込み中...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Preset Categories */}
              {categories.map((category) => (
                <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900">{category.name}</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isCategoryFullySelected(category)}
                        ref={(el) => {
                          if (el) {
                            el.indeterminate = isCategoryPartiallySelected(category);
                          }
                        }}
                        onChange={() => handleCategoryToggle(category)}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-600">全選択</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {category.presets.map((preset) => {
                      const key = `preset:${preset.id}`;
                      const isSelected = selectedSizes.has(key);
                      return (
                        <label
                          key={preset.id}
                          className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handlePresetToggle(preset)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {preset.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {preset.width}×{preset.height}
                            </div>
                          </div>
                          {isSelected && (
                            <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Custom Size Section */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">カスタムサイズ</h3>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">幅 (px)</label>
                    <input
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      min="1"
                      max="10000"
                      placeholder="1024"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <span className="text-gray-400 pb-2">×</span>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">高さ (px)</label>
                    <input
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      min="1"
                      max="10000"
                      placeholder="1024"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <button
                    onClick={handleAddCustomSize}
                    disabled={!customWidth || !customHeight}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    追加
                  </button>
                </div>
              </div>
              {preview && (
                <section className="border border-purple-200 bg-purple-50/40 rounded-lg p-4" aria-label="配置プレビュー">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">配置プレビュー</h3>
                      <p className="text-xs text-gray-600">元画像を選択サイズの枠に収めた参考表示です。AIによる生成結果や媒体の審査結果を再現するものではありません。</p>
                    </div>
                    <TaskySelect
                      aria-label="プレビューする媒体とサイズ"
                      value={preview.key}
                      onChange={(event) => setPreviewKey(event.target.value)}
                      className="w-auto h-auto py-2 text-sm"
                    >
                      {previewOptions.map((option) => <option key={option.key} value={option.key}>{option.label} / {option.width}×{option.height}</option>)}
                    </TaskySelect>
                  </div>
                  <div className="flex justify-center rounded-md bg-slate-100 p-3 min-h-56 items-center">
                    <div
                      className="relative overflow-hidden bg-white shadow-sm border border-slate-300"
                      style={{ aspectRatio: `${preview.width} / ${preview.height}`, width: `min(100%, ${Math.min(420, 280 * preview.width / preview.height)}px)`, maxHeight: 280 }}
                    >
                      {imageUrl ? <img src={imageUrl} alt="リサイズ元画像" className="w-full h-full object-contain" /> : <div className="flex h-full items-center justify-center text-xs text-gray-500">元画像の表示は利用できません</div>}
                      {guideInset > 0 && <div aria-hidden="true" className="absolute border-2 border-dashed border-amber-500 pointer-events-none" style={{ inset: `${guideInset}%` }} />}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                    <label htmlFor="resize-guide-inset" className="text-gray-700">確認用ガイド</label>
                    <TaskySelect id="resize-guide-inset" value={guideInset} onChange={(event) => setGuideInset(Number(event.target.value))} className="w-auto h-auto py-1">
                      {[0, 5, 10, 15, 20].map((value) => <option key={value} value={value}>{value === 0 ? '非表示' : `四辺から ${value}%`}</option>)}
                    </TaskySelect>
                    <span className="text-xs text-gray-500">この点線は任意の目安で、媒体の公式セーフゾーンではありません。</span>
                  </div>
                  <p className="mt-3 text-sm text-amber-900">{placementNote} 点線の外側や画像の端に文字・ロゴがある場合は、生成後の画像で重なり・欠けを確認してください。</p>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            選択中: <span className="font-semibold text-purple-600">{selectedSizes.size}</span> サイズ
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={executing}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              キャンセル
            </button>
            <button
              onClick={handleExecute}
              disabled={selectedSizes.size === 0 || executing}
              className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white rounded-md transition-all disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {executing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  実行中...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {selectedSizes.size}サイズを一括生成
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MagicResizeModal;
